import { NativeError, RuntimeError } from '@belimo-retrofit-portal/logic';
import { saveAs } from 'file-saver';
import { SagaIterator } from 'redux-saga';
import { NATIVE_BLOB } from 'src/io/codecs/NativeBlob';
import { XlsxValue } from 'src/modules/common/types/XlsxValue';
import { sentryCatch } from 'src/modules/config/utils/sentryCatch';
import { showNotification } from 'src/modules/notifications/sagas/showNotification';
import { PROJECT_LIST_EXPORT_ONE } from 'src/modules/project-list/actions/ProjectListActions';
import { EXPORT_CONTENT_LINE } from 'src/modules/project-list/codecs/export';
import { EXPORT_HEADER_COLUMNS } from 'src/modules/project-list/constants/export';
import { getProjectListExportEntry } from 'src/modules/project-list/selectors/getProjectListExportEntry';
import { getExportProjectFilename } from 'src/modules/project-list/utils/getExportProjectFilename';
import { createWorkerHandler } from 'src/modules/worker/utils/createWorkerHandler';
import { logDebug, logError } from 'src/sagas/utils/logging';
import { GetRequestActionType } from 'src/utils/createActions';
import { call, put, select } from 'typed-redux-saga';

export function* projectListExportOneSaga(
  action: GetRequestActionType<typeof PROJECT_LIST_EXPORT_ONE>,
): SagaIterator<void> {
  const project = action.meta;

  try {
    yield* put(PROJECT_LIST_EXPORT_ONE.pending(project));
    yield* call(logDebug, 'Exporting project...');

    const exportItem = yield* select(getProjectListExportEntry, project);
    const exportLine = yield* call(EXPORT_CONTENT_LINE.encode, exportItem);

    const exportData: XlsxValue[][] = [
      EXPORT_HEADER_COLUMNS.slice(),
      exportLine,
    ];

    const fileData = yield* call(doOnWorker, exportData);
    const fileName = yield* call(getExportProjectFilename, project, new Date());
    yield* call(saveAs, fileData, `${fileName}.xlsx`);

    yield* call(logDebug, 'Exporting project... done', exportData);
    yield* put(PROJECT_LIST_EXPORT_ONE.success(undefined, project));
  } catch (error) {
    const wrapped = new RuntimeError('Could not export project', { project }, NativeError.wrap(error));
    yield* call(sentryCatch, wrapped);
    yield* call(showNotification, { variant: 'error', type: 'error' });

    yield* call(logError, 'Exporting project... error', error);
    yield* put(PROJECT_LIST_EXPORT_ONE.failure(NativeError.wrap(error), project));
  }
}

const doOnWorker = createWorkerHandler(
  '/export-worker.js',
  NATIVE_BLOB,
)<ReadonlyArray<ReadonlyArray<XlsxValue>>>;
